import {
  CameraAlt as CameraAltIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Rating,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { clientApi } from '../services/clientApi';
import { dietitianApi } from '../services/dietitianApi';
import { ratingApi } from '../services/ratingApi';
import CancelButton from './CancelButton';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  onProfileUpdate?: () => void; // Callback to refresh parent component
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  onProfileUpdate,
}) => {
  const userEmail = localStorage.getItem('userEmail') || '';
  const userRole = localStorage.getItem('userRole') || '';
  const [userId, setUserId] = useState('');

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: userEmail,
    phone: '',
    bio: '',
    profileImage: '',
    // Diyetisyen için ek alanlar
    specialization: '',
    education: '',
    certifications: '',
    experienceYears: '',
    languages: '',
    acceptsOnlineClients: false,
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [ratingSummary, setRatingSummary] = useState<{
    averageScore: number;
    totalCount: number;
  } | null>(null);

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (userRole === 'client') {
        const clientData = await clientApi.getClientByEmail(userEmail);
        setUserId(clientData.id);
        setProfileImageFile(null);
        setProfileData((prev) => ({
          ...prev,
          firstName: clientData.firstName || '',
          lastName: clientData.lastName || '',
          email: clientData.email || '',
          phone: clientData.phone || '',
          bio: clientData.notes || '',
          profileImage: clientData.profileImageUrl || '',
        }));
      } else if (userRole === 'dietitian') {
        try {
          const dietitianData = await dietitianApi.getDietitianByEmail(
            userEmail
          );
          setUserId(dietitianData.id);
          setProfileImageFile(null);
          setProfileData((prev) => ({
            ...prev,
            firstName: dietitianData.firstName || '',
            lastName: dietitianData.lastName || '',
            email: dietitianData.email || '',
            phone: dietitianData.phone || '',
            bio: dietitianData.bio || '',
            profileImage: dietitianData.profileImageUrl || '',
            specialization: dietitianData.specialization || '',
            education: dietitianData.education || '',
            certifications: dietitianData.certifications || '',
            experienceYears: dietitianData.experienceYears != null ? String(dietitianData.experienceYears) : '',
            languages: dietitianData.languages || '',
            acceptsOnlineClients: dietitianData.acceptsOnlineClients ?? false,
          }));
        } catch (err) {
          // Diyetisyen henüz oluşturulmamış, boş başlat
          console.log('Dietitian not found, initializing empty profile');
          setProfileImageFile(null);
          setProfileData((prev) => ({
            ...prev,
            firstName: '',
            lastName: '',
            email: userEmail,
            phone: '',
            bio: '',
            profileImage: '',
            specialization: '',
            education: '',
            certifications: '',
            experienceYears: '',
            languages: '',
            acceptsOnlineClients: false,
          }));
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Profil bilgileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [userEmail, userRole]);

  // Load user data when modal opens
  useEffect(() => {
    if (open && userEmail) {
      loadUserData();
    }
  }, [open, userEmail, loadUserData]);

  // Diyetisyen kendi puan özetini görsün (profil modal açıkken)
  useEffect(() => {
    if (!open || userRole !== 'dietitian' || !userId) {
      setRatingSummary(null);
      return;
    }
    ratingApi
      .getSummary(userId)
      .then(setRatingSummary)
      .catch(() => setRatingSummary(null));
  }, [open, userRole, userId]);

  const handleInputChange = (field: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Dosya boyutu en fazla 5MB olabilir.');
        return;
      }
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData((prev) => ({
          ...prev,
          profileImage: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
      setError('');
    }
    event.target.value = '';
  };

  const uploadProfileImageToStorage = async (
    file: File,
    role: 'dietitian' | 'client',
    id: string
  ): Promise<string> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `profile-images/${role === 'dietitian' ? 'dietitians' : 'clients'}/${id}/avatar.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      let profileImageUrlToSave: string | undefined =
        profileData.profileImage?.startsWith('http')
          ? profileData.profileImage
          : undefined;

      if (profileImageFile && userId) {
        setUploadingImage(true);
        try {
          profileImageUrlToSave = await uploadProfileImageToStorage(
            profileImageFile,
            userRole as 'dietitian' | 'client',
            userId
          );
        } catch (uploadErr) {
          console.error('Profil resmi yüklenirken hata:', uploadErr);
          setError('Profil resmi yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
          setLoading(false);
          setUploadingImage(false);
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      if (userRole === 'client') {
        await clientApi.updateClient(userId, {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
          notes: profileData.bio,
          profileImageUrl: profileImageUrlToSave,
        });
      } else if (userRole === 'dietitian') {
        try {
          if (userId) {
            await dietitianApi.updateDietitian(userId, {
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              phone: profileData.phone,
              bio: profileData.bio,
              profileImageUrl: profileImageUrlToSave,
              specialization: profileData.specialization || undefined,
              education: profileData.education || undefined,
              certifications: profileData.certifications || undefined,
              experienceYears: profileData.experienceYears ? parseInt(profileData.experienceYears, 10) : undefined,
              languages: profileData.languages || undefined,
              acceptsOnlineClients: profileData.acceptsOnlineClients,
            });
          } else {
            const newDietitian = await dietitianApi.createDietitian({
              email: userEmail,
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              phone: profileData.phone,
              bio: profileData.bio,
              acceptsOnlineClients: profileData.acceptsOnlineClients,
            });
            setUserId(newDietitian.id);
            if (profileImageFile) {
              setUploadingImage(true);
              try {
                const url = await uploadProfileImageToStorage(
                  profileImageFile,
                  'dietitian',
                  newDietitian.id
                );
                await dietitianApi.updateDietitian(newDietitian.id, {
                  profileImageUrl: url,
                });
              } finally {
                setUploadingImage(false);
              }
            }
          }
        } catch (err) {
          console.error('Error saving dietitian data:', err);
          throw err;
        }
      }

      setSuccess(true);
      if (onProfileUpdate) onProfileUpdate();
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Profil güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (profileData.firstName && profileData.lastName) {
      return `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(
        0
      )}`.toUpperCase();
    }
    return userEmail.charAt(0).toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon sx={{ color: '#43A047' }} />
            <Typography variant="h6" fontWeight={700}>
              Profil Bilgilerim
            </Typography>
          </Box>
          <IconButton
            sx={{ width: 36, height: 36 }}
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {loading && !success ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <CircularProgress sx={{ color: '#43A047' }} />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Profile Picture Section */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <Box position="relative">
                <Avatar
                  src={profileData.profileImage}
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    background:
                      'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                    border: '4px solid #f5f7fa',
                    boxShadow: '0 8px 24px rgba(67, 160, 71, 0.25)',
                  }}
                >
                  {getInitials()}
                </Avatar>
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#43A047',
                    color: 'white',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      backgroundColor: '#2E7D32',
                    },
                  }}
                >
                  <CameraAltIcon fontSize="small" />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </IconButton>
              </Box>
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Profil fotoğrafı yükleyin
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG veya GIF (max. 5MB)
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Personal Information */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="#43A047"
                mb={2}
              >
                Kişisel Bilgiler
              </Typography>
              <Stack spacing={2}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Ad"
                    value={profileData.firstName}
                    onChange={(e) =>
                      handleInputChange('firstName', e.target.value)
                    }
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <PersonIcon sx={{ mr: 1, color: '#43A047' }} />
                      ),
                    }}
                  />
                  <TextField
                    label="Soyad"
                    value={profileData.lastName}
                    onChange={(e) =>
                      handleInputChange('lastName', e.target.value)
                    }
                    fullWidth
                  />
                </Box>

                <TextField
                  label="E-posta"
                  value={profileData.email}
                  disabled
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <EmailIcon sx={{ mr: 1, color: '#43A047' }} />
                    ),
                  }}
                  helperText="E-posta adresi değiştirilemez"
                />

                <TextField
                  label="Telefon"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  fullWidth
                  placeholder="+90 5XX XXX XX XX"
                  InputProps={{
                    startAdornment: (
                      <PhoneIcon sx={{ mr: 1, color: '#43A047' }} />
                    ),
                  }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Bio Section */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="#43A047"
                mb={2}
              >
                Hakkımda
              </Typography>
              <TextField
                label={
                  userRole === 'dietitian'
                    ? 'Uzmanlık ve Deneyim'
                    : 'Kendiniz Hakkında'
                }
                value={profileData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder={
                  userRole === 'dietitian'
                    ? 'Uzmanlık alanlarınız, deneyiminiz ve yaklaşımınız hakkında bilgi verin...'
                    : 'Sağlık hedefleriniz ve beklentileriniz hakkında bilgi verin...'
                }
              />
            </Box>

            {/* Diyetisyen: Puan özeti + Eğitim & Kariyer (sadece diyetisyen için) */}
            {userRole === 'dietitian' && (
              <>
                <Divider />
                {/* Danışan Puanınız - diyetisyen kendi ortalamasını görsün */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(67, 160, 71, 0.05)',
                    border: '1px solid rgba(67, 160, 71, 0.2)',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#43A047"
                    mb={1.5}
                  >
                    Danışan Puanınız
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Rating
                      value={ratingSummary?.averageScore ?? 0}
                      readOnly
                      size="small"
                      precision={0.1}
                      icon={<StarIcon sx={{ fontSize: 22, color: '#43A047' }} />}
                      emptyIcon={<StarIcon sx={{ fontSize: 22, color: '#dbdbdb' }} />}
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {ratingSummary && ratingSummary.totalCount > 0
                        ? `${ratingSummary.averageScore.toFixed(1)} · ${ratingSummary.totalCount} oy`
                        : 'Henüz oy yok'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Danışanlarınızın değerlendirmeleri anonim olarak toplanır.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#43A047"
                    mb={2}
                  >
                    Eğitim & Kariyer
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Uzmanlık Alanı"
                      value={profileData.specialization}
                      onChange={(e) =>
                        handleInputChange('specialization', e.target.value)
                      }
                      fullWidth
                      placeholder="Örn: Spor Beslenmesi, Klinik Beslenme"
                    />
                    <TextField
                      label="Eğitim Bilgileri"
                      value={profileData.education}
                      onChange={(e) =>
                        handleInputChange('education', e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Örn: İstanbul Üniversitesi, Beslenme ve Diyetetik Bölümü, 2015"
                    />
                    <TextField
                      label="Sertifikalar"
                      value={profileData.certifications}
                      onChange={(e) =>
                        handleInputChange('certifications', e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Örn: Spor Beslenmesi Sertifikası, Obezite Danışmanlığı"
                    />
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                      <TextField
                        type="number"
                        inputProps={{ min: 0, max: 50 }}
                        label="Deneyim (Yıl)"
                        value={profileData.experienceYears}
                        onChange={(e) =>
                          handleInputChange('experienceYears', e.target.value)
                        }
                        fullWidth
                        placeholder="Örn: 8"
                      />
                      <TextField
                        label="Çalıştığı Diller"
                        value={profileData.languages}
                        onChange={(e) =>
                          handleInputChange('languages', e.target.value)
                        }
                        fullWidth
                        placeholder="Örn: Türkçe, İngilizce"
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(67, 160, 71, 0.05)',
                        border: '1px solid rgba(67, 160, 71, 0.2)',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={profileData.acceptsOnlineClients}
                            onChange={(e) =>
                              setProfileData((prev) => ({
                                ...prev,
                                acceptsOnlineClients: e.target.checked,
                              }))
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              Online Danışan Almak İstiyorum
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Açarsanız danışanlar sizi seçebilir ve iletişime geçebilir.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Stack>
                </Box>
              </>
            )}

            {/* User Info Card */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background:
                  'linear-gradient(135deg, #43A04715 0%, #2E7D3215 100%)',
                border: '1px solid #43A04730',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
                mb={0.5}
              >
                Hesap Tipi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userRole === 'dietitian'
                  ? '👨‍⚕️ Diyetisyen Hesabı'
                  : '👤 Danışan Hesabı'}
              </Typography>
            </Box>

            {/* Alerts */}
            {success && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Profil bilgileriniz başarıyla güncellendi!
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <CancelButton
                onClick={onClose}
                disabled={loading || uploadingImage}
              />
              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSave}
                disabled={loading || uploadingImage}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background:
                    'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                  boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(67, 160, 71, 0.4)',
                  },
                }}
              >
                {loading ? 'Kaydediliyor...' : uploadingImage ? 'Resim yükleniyor...' : 'Kaydet'}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
